import { useAuth } from '@/features/auth/session';

/**
 * Presentation-only PLATFORM-scope capability derivation (G-9, Job 6),
 * mirroring `lib/capabilities.ts`'s existing organization-scope pattern
 * exactly. Transcribed verbatim from `server/src/authz/permissions.ts`'s
 * own `PLATFORM_ROLE_PERMISSION_LIST` - no invented capability.
 *
 * This is NOT the security boundary (same disclaimer as capabilities.ts):
 * the backend re-derives and enforces every permission independently.
 * Gating a ROUTE on this (`RequirePlatformCapability`) only avoids
 * rendering a page the backend would reject every mutation from; it is
 * not itself the authorization check.
 *
 * Reads `platformMemberships` directly off `CurrentUser` (unlike
 * `useHasCapability`, which reads the Active Organization) - a Platform
 * Super Admin may legitimately hold zero organization memberships, which
 * is exactly the case this module exists to support (capabilities.ts's
 * own "Known limitation" note).
 */

export type PlatformCapability =
  'platform:read' | 'platform:manage' | 'organizations:read' | 'organizations:manage';

/** Transcribed from `server/src/authz/permissions.ts`'s `PLATFORM_ROLE_PERMISSION_LIST`. */
const PLATFORM_ROLE_CAPABILITIES: Readonly<Record<string, readonly PlatformCapability[]>> = {
  'Platform Super Admin': [
    'platform:read',
    'platform:manage',
    'organizations:read',
    'organizations:manage',
  ],
};

const EMPTY_SET: ReadonlySet<PlatformCapability> = new Set();

export function capabilitiesForPlatformRole(roleName: string): ReadonlySet<PlatformCapability> {
  const list = PLATFORM_ROLE_CAPABILITIES[roleName];
  return list === undefined ? EMPTY_SET : new Set(list);
}

/**
 * Whether ANY of the current user's platform memberships carries
 * `capability`. `false` for every state where that cannot be determined
 * (unauthenticated, no platform membership, unknown role) - default deny.
 */
export function useHasPlatformCapability(capability: PlatformCapability): boolean {
  const { user } = useAuth();
  if (user === null) {
    return false;
  }
  return user.platformMemberships.some((membership) =>
    capabilitiesForPlatformRole(membership.roleName).has(capability),
  );
}
